import { useQueryClient } from '@tanstack/react-query';
import { Rocket } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';

import { useTaxonomyStudio } from '../../context/taxonomy-studio-context';
import { usePublishDraft } from '../../hooks/use-draft-mutations';
import { useFormErrors, useTaxonomyErrors } from '../../hooks/use-taxonomy-errors';
import type { DraftChecks, Release } from '../../types/taxonomy-studio.types';
import { readCheckErrors, readProblem } from '../../utils/taxonomy-errors';
import { opTone } from '../../utils/taxonomy-tones';
import { DisabledReason } from '../shared/disabled-reason';
import { FieldError } from '../shared/field-error';
import { TaxPill } from '../shared/tax-pill';

interface PublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: Release;
  checks: DraftChecks | undefined;
}

/**
 * Publish the draft: name + notes, a by-op summary and the checks. Sends
 * `expected_draft_version` = the version on screen; a 409 DRAFT_CHANGED means someone
 * edited meanwhile, so the dialog reloads and asks for a second look.
 */
export function PublishDialog(props: PublishDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      {props.open && <PublishDialogBody {...props} />}
    </Dialog>
  );
}

function PublishDialogBody({ onOpenChange, draft, checks }: PublishDialogProps) {
  const { t } = useTranslation('taxonomy-studio');
  const qc = useQueryClient();
  const { live, setReleaseRef, acknowledgeOthers } = useTaxonomyStudio();
  const id = useId();
  const [name, setName] = useState(draft.name);
  const [notes, setNotes] = useState(draft.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const publish = usePublishDraft();
  const { notify } = useTaxonomyErrors();
  const formErrors = useFormErrors();

  const errors = checks?.errors.length ?? 0;
  const blocked = errors > 0 || draft.changeCount === 0;

  const submit = () => {
    setError(null);
    formErrors.clear();
    publish.mutate(
      {
        name: name.trim() || undefined,
        notes: notes.trim() || undefined,
        expectedDraftVersion: draft.draftVersion ?? 0,
      },
      {
        onSuccess: (release) => {
          toast.success(t('publish.done', { seq: release.seq }));
          setReleaseRef(undefined);
          onOpenChange(false);
        },
        onError: (err) => {
          const p = readProblem(err);
          if (p.code === 'DRAFT_CHANGED') {
            // Someone edited after this review: reload everything and ask for a second look.
            setError(t('publish.draftChanged'));
            acknowledgeOthers();
          } else if (p.code === 'CHECKS_FAILED') {
            // Show what blocks it, with Go to links, instead of a bare message. The 409
            // carries the failing checks, so the popover shows them at once.
            const failing = readCheckErrors(err);
            if (failing.length > 0) {
              qc.setQueryData<DraftChecks>(queryKeys.taxonomyStudio.checks(), (prev) => ({
                draftVersion: prev?.draftVersion ?? 0,
                warnings: prev?.warnings ?? [],
                info: prev?.info ?? [],
                ready: false,
                errors: failing,
              }));
            } else {
              void qc.invalidateQueries({ queryKey: queryKeys.taxonomyStudio.checks() });
            }
            onOpenChange(false);
            notify(err);
          } else if (formErrors.capture(err)) {
            setError(formErrors.formFor(['name', 'notes']));
          } else {
            notify(err, { retry: submit });
          }
        },
      },
    );
  };

  const byOp = draft.summary.byOp;
  return (
    <DialogContent className="max-w-lg">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('publish.title')}</DialogTitle>
          <DialogDescription>
            {t('publish.description', { seq: draft.seq, live: live?.seq ?? '—' })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-name`}>{t('publish.name')}</Label>
          <Input
            id={`${id}-name`}
            value={name}
            autoComplete="off"
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <FieldError message={formErrors.fields.name} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-notes`}>{t('publish.notes')}</Label>
          <Textarea
            id={`${id}-notes`}
            rows={3}
            value={notes}
            placeholder={t('publish.notesPlaceholder')}
            onChange={(e) => {
              setNotes(e.target.value);
            }}
          />
          <FieldError message={formErrors.fields.notes} />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(byOp)
            .filter(([, n]) => n > 0)
            .map(([op, n]) => (
              <TaxPill key={op} tone={opTone(op)}>
                {String(n)} {t(`op.${op}`, { defaultValue: op }).toLowerCase()}
              </TaxPill>
            ))}
        </div>
        {draft.changeCount === 0 && (
          <p className="text-warning text-sm">{t('publish.noChanges')}</p>
        )}
        {errors > 0 ? (
          <p className="text-error text-sm">{t('publish.blocked', { count: errors })}</p>
        ) : (
          <p className="text-success text-sm">{t('checks.allPass')}</p>
        )}
        {error && (
          <p role="alert" className="bg-error-50 text-error rounded-md px-3 py-2 text-sm">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="cursor-pointer"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t('actions.cancel')}
          </Button>
          <DisabledReason
            reason={
              draft.changeCount === 0
                ? t('disabled.nothingToPublish')
                : errors > 0
                  ? t('disabled.checksFailing', { count: errors })
                  : null
            }
          >
            <Button
              type="submit"
              className="cursor-pointer"
              disabled={blocked}
              isLoading={publish.isPending}
            >
              <Rocket />
              {t('publish.confirm', { seq: draft.seq })}
            </Button>
          </DisabledReason>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
